import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Text from 'design-system/Text';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import { AppView, ButtonGroup, hitSlop, rem, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import links from 'utils/links';
import { useAppSelector } from 'store/hooks';
import { selectUser } from 'reducers/selectors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import Animated, { useAnimatedReaction, useAnimatedScrollHandler, useDerivedValue, useSharedValue, } from 'react-native-reanimated';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useStockMeta } from '../hooks';
import useOrderCache from '../hooks/useOrderCache';
import Receipt from '../components/Receipt';
import { selectActiveTradingAccountId } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const EachOrderDetails = ({ orderId, assetId, scrollY, width, }) => {
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const order = useOrderCache(orderId, tradingAccountId);
    const stock = useStockMeta(assetId);
    const user = useAppSelector(selectUser);
    const navigation = useMainStackNavigation();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, styles.container);
    const title = useMemo(() => {
        if (order) {
            const name = stock?.name || order.assetId;
            const prefix = order.operation === 'buy' ? 'Buy' : 'Sell';
            if (name) {
                return `${prefix} ${name}`;
            }
            return prefix;
        }
        return '';
    }, [order, stock?.name]);
    const subTitle = useMemo(() => {
        if (order) {
            if (order?.status === 'completed' || order?.status === 'partially_filled') {
                return `This ${order.operation === 'buy' ? 'buy' : 'sell'} order is executed by the Switch`;
            }
            if (order?.status === 'failed') {
                return `This ${order.operation} order failed to execute`;
            }
            if (order?.status === 'cancelled') {
                return `This ${order.operation} order was cancelled`;
            }
        }
        return undefined;
    }, [order]);
    const ConfirmationButton = useMemo(() => {
        const url = order?.confirmation?.url;
        if (url) {
            return (<View style={styles.flexCenteredWithPadding}>
          <TouchableBounce onPress={() => {
                    if (Platform.OS === 'android') {
                        navigation.navigate('Pdf', { url, withTopPadding: true });
                    }
                    else {
                        links.openLink(url);
                    }
                }} hitSlop={hitSlop}>
            <Text Text-16 Primary>
              View trade confirmation
            </Text>
          </TouchableBounce>
        </View>);
        }
        return null;
    }, [order?.confirmation?.url]);
    const rows = useMemo(() => {
        const out = {};
        const pricing = order?.filled || order?.estimated;
        const isEstimated = !order?.filled;
        if (pricing) {
            out.clientName = user.firstName ? `${user.firstName} ${user.lastName}` : user.lastName;
            // eslint-disable-next-line prefer-destructuring
            out.orderId = orderId.split('-')[0];
            if (!isEstimated) {
                out.numOfShares = pricing.quantity;
                out.pricePerShare = pricing.price;
                out.completeTime = order.filledTimestamp;
                const fee = { value: 0, currency: 'GBP' };
                out.tradingCharge = fee;
            }
            else {
                out.numOfSharesEst = pricing.quantity;
            }
            if (order.operation === 'sell') {
                out.totalAmount = pricing.amount;
            }
            else {
                out.totalCost = pricing.amount;
            }
        }
        else {
            return null;
        }
        return out;
    }, [
        order?.estimated,
        order?.filled,
        order?.filledTimestamp,
        order?.operation,
        orderId,
        user.firstName,
        user.lastName,
    ]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    }, []);
    return (<Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
      <Receipt alwaysShowStatus status={order?.status === 'partially_filled' ? 'completed' : order?.status} title={title} subtitle={subTitle} logoUrl={stock?.iconUrl} rows={rows} width={width}/>
      {ConfirmationButton}
    </Animated.ScrollView>);
};
const SwitchOrderDetails = withAnimatedNavigationHeader(({ route, navigation, scrollY }) => {
    const onClose = useCallback(() => {
        navigation.goBack();
    }, [navigation]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => <NavigationCloseButton inWebHeader onPress={onClose}/>,
        });
    }, [navigation, onClose]);
    const scrollX = useSharedValue(0);
    const scrollYBuy = useSharedValue(0);
    const scrollYSell = useSharedValue(0);
    const { width, paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const widthOfCard = width - rem(32);
    const currentIndex = useDerivedValue(() => Math.round(scrollX.value / widthOfCard), [scrollX, widthOfCard]);
    useAnimatedReaction(() => (currentIndex.value === 0 ? scrollYSell.value : scrollYBuy.value), (value) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = value;
    });
    const scrollXHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollX.value = contentOffset.x;
    }, []);
    const { id, switchOrderId, assetId, switchAssetId } = route.params;
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView horizontal pagingEnabled decelerationRate="fast" onScroll={scrollXHandler} snapToInterval={widthOfCard} showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.contentContainer, paddingHorizontalStyle]}>
          <View style={[styles.flex, { width: widthOfCard }]}>
            <EachOrderDetails orderId={id} assetId={assetId} scrollY={scrollYSell} width={widthOfCard}/>
          </View>
          <View style={[styles.flex, { width: widthOfCard }]}>
            <EachOrderDetails orderId={switchOrderId} assetId={switchAssetId} scrollY={scrollYBuy} width={widthOfCard}/>
          </View>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button brand title="Close" onPress={onClose}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    contentContainer: {
        flexGrow: 1,
    },
    flex: {
        flex: 1,
    },
    flexCenteredWithPadding: {
        alignItems: 'center',
        marginTop: rem(16),
    },
    container: {
        marginHorizontal: rem(16),
    },
});
export default SwitchOrderDetails;
